<template>
  <section
    class="card p-4 space-y-3 landing-section-editor"
    :data-section-type="section.type"
    :aria-labelledby="headingId">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <h3 :id="headingId" class="font-semibold" style="color: var(--text-primary);">
        {{ title }}
      </h3>
      <div v-if="!readOnly" class="flex flex-wrap gap-1">
        <button
          v-if="canMoveUp"
          type="button"
          class="btn btn-secondary btn-sm"
          data-action="move-up"
          :aria-label="`Move ${title} up`"
          @click="$emit('move', -1)">
          Move up
        </button>
        <button
          v-if="canMoveDown"
          type="button"
          class="btn btn-secondary btn-sm"
          data-action="move-down"
          :aria-label="`Move ${title} down`"
          @click="$emit('move', 1)">
          Move down
        </button>
        <button
          v-if="canRemove"
          type="button"
          class="btn btn-secondary btn-sm"
          data-action="remove-section"
          :aria-label="`Remove ${title}`"
          @click="$emit('remove')">
          Remove
        </button>
      </div>
    </div>

    <p v-for="message in sectionErrors" :key="message" class="text-sm text-red-700" role="alert">
      {{ message }}
    </p>

    <template v-for="key in fieldKeys" :key="key">
      <div v-if="key === 'items'" class="space-y-2">
        <p class="text-sm font-medium" style="color: var(--text-secondary);">
          {{ itemsLabel }}
        </p>
        <fieldset
          v-for="(item, itemIndex) in items"
          :key="itemIndex"
          class="rounded-lg border p-3 space-y-2"
          style="border-color: var(--border-primary, #e5e7eb);">
          <legend class="text-xs px-1" style="color: var(--text-secondary);">
            Item {{ itemIndex + 1 }}
          </legend>
          <template v-for="itemKey in itemKeys" :key="itemKey">
            <div v-if="isStringList(itemSchema(itemKey))">
              <StringListField
                :label="labelFor(itemKey)"
                :path="`items.${itemIndex}.${itemKey}`"
                :values="item[itemKey] || []"
                :max-items="itemSchema(itemKey).maxItems"
                :max-length="itemSchema(itemKey).items?.maxLength"
                :read-only="readOnly"
                @update="setItem(itemIndex, itemKey, $event)" />
              <span
                v-for="message in errorsUnder(`items/${itemIndex}/${itemKey}`)"
                :key="message"
                class="block text-xs text-red-700">{{ message }}</span>
            </div>
            <label v-else-if="isBoolean(itemSchema(itemKey))" class="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                :data-field="`items.${itemIndex}.${itemKey}`"
                :checked="item[itemKey] === true"
                :disabled="readOnly"
                @change="setItem(itemIndex, itemKey, $event.target.checked)">
              <span class="font-medium" style="color: var(--text-secondary);">{{ labelFor(itemKey) }}</span>
            </label>
            <label v-else class="flex flex-col text-sm">
              <span class="font-medium" style="color: var(--text-secondary);">{{ labelFor(itemKey) }}</span>
              <select
                v-if="isRating(itemKey, itemSchema(itemKey))"
                class="form-select mt-1"
                :data-field="`items.${itemIndex}.${itemKey}`"
                :value="item[itemKey] == null ? '' : String(item[itemKey])"
                :disabled="readOnly"
                @change="setItem(itemIndex, itemKey, $event.target.value === '' ? undefined : Number($event.target.value))">
                <option value="">
                  No rating
                </option>
                <option v-for="stars in [1, 2, 3, 4, 5]" :key="stars" :value="String(stars)">
                  {{ stars }} of 5
                </option>
              </select>
              <select
                v-else-if="isImage(itemKey)"
                class="form-select mt-1"
                :data-field="`items.${itemIndex}.${itemKey}`"
                :value="item[itemKey] || ''"
                :disabled="readOnly"
                @change="setItem(itemIndex, itemKey, $event.target.value || undefined)">
                <option value="">
                  Choose an image
                </option>
                <option v-if="item[itemKey] && !assetIds.has(item[itemKey])" :value="item[itemKey]">
                  Current image
                </option>
                <option v-for="asset in assets" :key="asset.id" :value="asset.id">
                  {{ assetLabel(asset) }}
                </option>
              </select>
              <textarea
                v-else-if="multiline(itemSchema(itemKey))"
                class="form-input mt-1"
                rows="3"
                :data-field="`items.${itemIndex}.${itemKey}`"
                :value="item[itemKey] ?? ''"
                :disabled="readOnly"
                :maxlength="itemSchema(itemKey).maxLength"
                @input="setItem(itemIndex, itemKey, $event.target.value)" />
              <input
                v-else
                class="form-input mt-1"
                type="text"
                :data-field="`items.${itemIndex}.${itemKey}`"
                :value="item[itemKey] ?? ''"
                :disabled="readOnly"
                :maxlength="itemSchema(itemKey).maxLength"
                @input="setItem(itemIndex, itemKey, $event.target.value)">
              <span
                v-for="message in errorsFor(`items/${itemIndex}/${itemKey}`)"
                :key="message"
                class="text-xs text-red-700">{{ message }}</span>
            </label>
          </template>
          <div v-if="!readOnly" class="flex flex-wrap gap-1">
            <button
              v-if="itemIndex > 0"
              type="button"
              class="btn btn-secondary btn-sm"
              data-action="move-item-up"
              :aria-label="`Move item ${itemIndex + 1} up`"
              @click="moveItem(itemIndex, -1)">
              Move up
            </button>
            <button
              v-if="itemIndex < items.length - 1"
              type="button"
              class="btn btn-secondary btn-sm"
              data-action="move-item-down"
              :aria-label="`Move item ${itemIndex + 1} down`"
              @click="moveItem(itemIndex, 1)">
              Move down
            </button>
            <button
              v-if="items.length > minItems"
              type="button"
              class="btn btn-secondary btn-sm"
              data-action="remove-item"
              :aria-label="`Remove item ${itemIndex + 1}`"
              @click="removeItem(itemIndex)">
              Remove item
            </button>
          </div>
        </fieldset>
        <button
          v-if="!readOnly && items.length < maxItems"
          type="button"
          class="btn btn-secondary btn-sm"
          data-action="add-item"
          @click="addItem">
          Add item
        </button>
      </div>

      <div v-else-if="isStringList(schemaFor(key))">
        <StringListField
          :label="labelFor(key)"
          :path="key"
          :values="fields[key] || []"
          :max-items="schemaFor(key).maxItems"
          :max-length="schemaFor(key).items?.maxLength"
          :read-only="readOnly"
          @update="setField(key, $event)" />
        <span v-for="message in errorsUnder(key)" :key="message" class="block text-xs text-red-700">{{ message }}</span>
      </div>

      <label v-else-if="isBoolean(schemaFor(key))" class="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          :data-field="key"
          :checked="fields[key] === true"
          :disabled="readOnly"
          @change="setField(key, $event.target.checked)">
        <span class="font-medium" style="color: var(--text-secondary);">{{ labelFor(key) }}</span>
      </label>

      <div v-else-if="isImage(key)" class="text-sm">
        <span class="font-medium" style="color: var(--text-secondary);">{{ labelFor(key) }}</span>
        <p class="mt-1" style="color: var(--text-primary);" :data-field="key">
          {{ fields[key] ? 'Image selected' : 'No image' }}
        </p>
        <div v-if="!readOnly" class="flex flex-wrap items-center gap-2 mt-1">
          <label v-if="uploadAsset" class="btn btn-secondary btn-sm focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
            {{ uploading ? 'Uploading…' : 'Upload image' }}
            <input
              type="file"
              class="sr-only"
              accept="image/jpeg,image/png,image/webp,image/avif"
              :data-field="`${key}-upload`"
              :disabled="uploading"
              @change="upload(key, $event)">
          </label>
          <button
            v-if="fields[key]"
            type="button"
            class="btn btn-secondary btn-sm"
            :data-action="`clear-${key}`"
            @click="setField(key, null)">
            Remove image
          </button>
        </div>
        <span v-for="message in errorsFor(key)" :key="message" class="block text-xs text-red-700">{{ message }}</span>
      </div>

      <label v-else class="flex flex-col text-sm">
        <span class="font-medium" style="color: var(--text-secondary);">{{ labelFor(key) }}</span>
        <input
          v-if="key === 'form_instance_id'"
          class="form-input mt-1"
          type="text"
          :data-field="key"
          :value="fields[key] ?? ''"
          disabled>
        <textarea
          v-else-if="multiline(schemaFor(key))"
          class="form-input mt-1"
          rows="3"
          :data-field="key"
          :value="fields[key] ?? ''"
          :disabled="readOnly"
          :maxlength="schemaFor(key).maxLength"
          @input="setField(key, $event.target.value)" />
        <input
          v-else
          class="form-input mt-1"
          :type="isLink(key) ? 'text' : inputType(key)"
          :inputmode="isLink(key) ? 'url' : undefined"
          :data-field="key"
          :value="fields[key] ?? ''"
          :disabled="readOnly"
          :maxlength="schemaFor(key).maxLength"
          @input="setField(key, $event.target.value)">
        <span v-if="isLink(key)" class="text-xs mt-1" style="color: var(--text-secondary);">
          A full https:// address, a page path like /contact, or a section link like #quote-form.
        </span>
        <span v-for="message in errorsFor(key)" :key="message" class="text-xs text-red-700">{{ message }}</span>
      </label>
    </template>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'
import StringListField from './StringListField.vue'
import { blankObject, isBoolean, isRating, isStringList } from './section_values'

const props = defineProps({
  section: { type: Object, required: true },
  definition: { type: Object, default: null },
  editor: { type: Object, default: null },
  errors: { type: Object, default: () => ({}) },
  index: { type: Number, default: 0 },
  readOnly: { type: Boolean, default: false },
  canMoveUp: { type: Boolean, default: false },
  canMoveDown: { type: Boolean, default: false },
  canRemove: { type: Boolean, default: false },
  uploadAsset: { type: Function, default: null },
  assets: { type: Array, default: () => []}
})

const emit = defineEmits(['update', 'move', 'remove', 'error'])

const SECTION_TITLES = {
  hero: 'Top of page',
  form: 'Contact form',
  contact: 'Contact details',
  services: 'Services',
  benefits: 'Benefits',
  process: 'How it works',
  service_area: 'Service area',
  faq: 'Questions and answers',
  trust: 'Confirmed information',
  offer: 'Offer',
  cta: 'Call to action',
  credentials: 'Credentials',
  locations: 'Locations',
  tiers: 'Options and prices',
  testimonials: 'Customer reviews',
  logo_bar: 'Logos'
}
const LABELS = {
  headline: 'Headline',
  summary: 'Summary',
  heading: 'Heading',
  body: 'Text',
  cta_label: 'Button label',
  cta_url: 'Button link',
  label: 'Button label',
  url: 'Button link',
  image_asset_id: 'Image',
  image_alt: 'Image description',
  submit_label: 'Submit button label',
  form_instance_id: 'Form',
  business_name: 'Business name',
  phone: 'Phone',
  email: 'Email',
  hours: 'Hours',
  address: 'Address',
  service_area: 'Service area',
  privacy_url: 'Privacy link',
  title: 'Title',
  description: 'Description',
  question: 'Question',
  answer: 'Answer',
  evidence_id: 'Evidence',
  name: 'Name',
  price_note: 'Price note',
  quote: 'Quote',
  detail: 'Detail',
  features: 'Included',
  highlighted: 'Highlight this option',
  rating: 'Star rating',
  areas: 'Areas served'
}

const uploading = ref(false)
const headingId = computed(() => `section-${props.index}-${props.section.id}`)
const title = computed(() => SECTION_TITLES[props.section.type] || props.section.type)
const fields = computed(() => props.section.fields || {})
const properties = computed(() => props.definition?.fields?.properties || {})
const required = computed(() => props.definition?.fields?.required || [])
const approved = computed(() => props.editor?.fields || null)
// Only fields the template approves (or, without editor metadata, the ones the
// document already uses plus the schema's required ones) are editable.
const fieldKeys = computed(() => {
  const keys = approved.value ?
    approved.value :
    [...new Set([...Object.keys(properties.value).filter((key) => key in fields.value || required.value.includes(key)),
      ...Object.keys(fields.value)])]
  return ordered(keys)
})
const itemsSchema = computed(() => properties.value.items || {})
const itemKeys = computed(() => {
  if (props.editor?.item_fields) return ordered(props.editor.item_fields)
  const fromSchema = Object.keys(itemsSchema.value.items?.properties || {})
  const present = [...new Set((fields.value.items || []).flatMap((item) => Object.keys(item || {})))]
  return ordered(present.length ? present : fromSchema)
})
const items = computed(() => fields.value.items || [])
const minItems = computed(() => props.editor?.min_items ?? itemsSchema.value.minItems ?? 0)
const maxItems = computed(() => props.editor?.max_items ?? itemsSchema.value.maxItems ?? 20)
const itemsLabel = computed(() => (props.section.type === 'faq' ? 'Questions' : 'Items'))
const sectionErrors = computed(() => props.errors[''] || [])

// Stored schemas lose key order (jsonb), so fields follow the reading order
// of a page section: heading copy first, then actions, media and structure.
const FIELD_ORDER = ['headline', 'heading', 'title', 'question', 'name', 'price_note', 'quote', 'summary', 'body', 'description',
  'answer', 'detail', 'features', 'highlighted', 'rating', 'label', 'cta_label', 'cta_url', 'url', 'submit_label', 'business_name',
  'phone', 'email', 'hours', 'address', 'service_area', 'areas', 'privacy_url', 'image_asset_id', 'image_alt', 'evidence_id', 'items',
  'form_instance_id']

function ordered(keys) {
  const rank = (key) => (FIELD_ORDER.includes(key) ? FIELD_ORDER.indexOf(key) : FIELD_ORDER.length)
  return [...keys].sort((left, right) => rank(left) - rank(right))
}

function labelFor(key) {
  return props.editor?.labels?.[key] || LABELS[key] || key.replace(/_/g, ' ').replace(/^./, (letter) => letter.toUpperCase())
}

function schemaFor(key) {
  return properties.value[key] || {}
}

function itemSchema(key) {
  return itemsSchema.value.items?.properties?.[key] || {}
}

function multiline(schema) {
  return (schema.maxLength || 0) > 200 && schema.format !== 'email' && !schema.pattern
}

function isImage(key) {
  return key.endsWith('_asset_id')
}

function isLink(key) {
  return key === 'url' || key.endsWith('_url')
}

function inputType(key) {
  if (key === 'email') return 'email'
  if (key === 'phone') return 'tel'
  return 'text'
}

function errorsFor(key) {
  return props.errors[key] || []
}

// A list's own errors and its entries' errors (e.g. items/0/features/1).
function errorsUnder(key) {
  return Object.entries(props.errors).filter(([path]) => path === key || path.startsWith(`${key}/`)).flatMap(([, messages]) => messages)
}

const assetIds = computed(() => new Set(props.assets.map((asset) => asset.id)))

function assetLabel(asset) {
  return asset.width && asset.height ? `${asset.filename} (${asset.width} × ${asset.height})` : asset.filename
}

function update(nextFields) {
  emit('update', { ...props.section, fields: nextFields })
}

function setField(key, value) {
  update({ ...fields.value, [key]: value })
}

// undefined removes the key (an unset optional rating or image).
function setItem(index, key, value) {
  const next = items.value.map((item, position) => {
    if (position !== index) return item
    const changed = { ...item, [key]: value }
    if (value === undefined) delete changed[key]
    return changed
  })
  update({ ...fields.value, items: next })
}

function addItem() {
  const blank = blankObject(itemKeys.value, itemSchema)
  update({ ...fields.value, items: [...items.value, blank]})
}

function removeItem(index) {
  update({ ...fields.value, items: items.value.filter((_item, position) => position !== index) })
}

function moveItem(index, offset) {
  const next = [...items.value]
  const [moved] = next.splice(index, 1)
  next.splice(index + offset, 0, moved)
  update({ ...fields.value, items: next })
}

async function upload(key, event) {
  const file = event.target.files?.[0]
  if (!file || !props.uploadAsset) return
  uploading.value = true
  try {
    const assetId = await props.uploadAsset(file)
    setField(key, assetId)
  } catch (error) {
    emit('error', error)
  } finally {
    uploading.value = false
    // eslint-disable-next-line no-param-reassign
    event.target.value = ''
  }
}
</script>
